import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { BALLOON } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { setProgramFallbackOverrides, updateCurrentCalculationEndpoint } from '../actions/offerRedux';
import { CALC_ENDPOINTS } from '../constants';
import * as offerReduxSelectors from '../selectors/offerRedux';

type RollToStore = {
    dispatch: (action: any) => unknown;
    getState: () => StateTree;
};

type RollToUpqSwitchResult = {
    switched: boolean;
    settled: Promise<void> | null;
};

const PRIMARY_SCOPE = '__primary';
export const SWITCH_SETTLE_TIMEOUT_MS = 30000;

const switchSettlers = new Map<string, () => void>();
const requestedSwitches = new Set<string>();
const pendingSwitchByScope = new Map<string, Promise<void>>();
const sessionByScope = new Map<string, number>();
let switchSeq = 0;

const scopeOf = (state: StateTree): string => (state as { __scopeId?: string } | undefined)?.__scopeId ?? PRIMARY_SCOPE;

const switchIdOf = (action: AnyFSA): string | undefined => action.meta?.originalAction?.meta?.rollToUpqSwitchId;

export const getRollToSession = (state: StateTree): number => sessionByScope.get(scopeOf(state)) ?? 0;

export const endRollToSession = (state: StateTree): void => {
    sessionByScope.set(scopeOf(state), getRollToSession(state) + 1);
};

export const recordRollToUpqSwitchRequest = (action: AnyFSA): void => {
    const id = switchIdOf(action);
    if (id) requestedSwitches.add(id);
};

const settleSwitch = (id: string): void => {
    const settle = switchSettlers.get(id);
    if (!settle) return;

    switchSettlers.delete(id);
    requestedSwitches.delete(id);
    settle();
};

export const settleRollToUpqSwitch = (action: AnyFSA): void => {
    const id = switchIdOf(action);
    if (id) settleSwitch(id);
};

export const ensureUserProgramQuotesForRollTo = ({ dispatch, getState }: RollToStore): RollToUpqSwitchResult => {
    const state = getState();
    if (!featureToggleSelectors.isRollToUpqOnFirstCalculationEnabled(state)) return { switched: false, settled: null };

    const scope = scopeOf(state);
    const pendingSwitch = pendingSwitchByScope.get(scope);
    if (pendingSwitch) return { switched: true, settled: pendingSwitch };

    const offerType = offerReduxSelectors.getCurrentOfferType(state);
    if (offerReduxSelectors.getCalculationEndpointByOfferType(state, offerType) === CALC_ENDPOINTS.userProgramQuotes) {
        return { switched: false, settled: null };
    }

    if (offerType === BALLOON) {
        dispatch(setProgramFallbackOverrides());
        dispatch(updateCurrentCalculationEndpoint(CALC_ENDPOINTS.userProgramQuotes, offerType));
        return { switched: true, settled: null };
    }

    const id = String(++switchSeq);
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const settled = new Promise<void>((resolve) => {
        switchSettlers.set(id, () => {
            clearTimeout(timeout);
            pendingSwitchByScope.delete(scope);
            resolve();
        });
    });
    pendingSwitchByScope.set(scope, settled);

    const switchAction = updateCurrentCalculationEndpoint(CALC_ENDPOINTS.userProgramQuotes, offerType, { withPayment: true });
    dispatch({ ...switchAction, meta: { ...switchAction.meta, rollToUpqSwitchId: id } });

    if (requestedSwitches.has(id)) {
        timeout = setTimeout(() => settleSwitch(id), SWITCH_SETTLE_TIMEOUT_MS);
    } else {
        settleSwitch(id);
    }

    return { switched: true, settled };
};
